import React, { FC, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Button } from "react-bootstrap";
import AiIcon from "../../assets/icons/Icon_Library/application icons/library/boomAI_icon.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPen,
  faX,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import SendIcon from "../../assets/icons/send.svg2";
import MyModal from "../Common/MyModal";
import SolutionStepInputGroup, {
  isComponentConfigComplete,
} from "./SolutionStepInputGroup";
import styled from "styled-components";
import VisibilityInputs from "../Builder/Components/modals/VisibilityInputs";
import DeferredEditForm from "./DeferredEditForm";
import SolutionAiSuggestionMarker from "./SolutionAiSuggestionMarker";

const StyledDiv = styled.div`
  .edit-buttons button {
    opacity: 0;
  }
  .edit-buttons:hover button {
    opacity: 1;
  }
`;

interface SolutionStepProps {
  step: any;
  stepIndex?: number;
  hasPreviousStep?: boolean;
  hasNextStep?: boolean;
  nextStepName?: string;
  disableInputEditing?: boolean;
  disableInputDragging?: boolean;
  focusedInputNodeKey?: string | null;
  onInputFocus?: (inputGroupId: string, input?: any) => void;
  onInputBlur?: (inputGroupId?: string, input?: any) => void;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  setStep: (step: any) => void;
  onDelete?: () => void;
  formData: Record<string, any>;
  setFormData?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  sidebarDragging?: boolean;
  sidebarOverInput?: any;
  importContext?: any;
  usedInputs?: any[];
  inputUsageByKey?: Record<string, any[]>;
  onUsageSelect?: (usage: any) => void;
  getIsVisible?: (vg: any) => boolean;
  showDefaultValueInputsOnly?: boolean;
  showShortlinksButton?: boolean;
  aiAssistantSuggestions?: any[];
  isAiAssistantLoading?: boolean;
  onAiAssistantSuggestionClick?: (suggestion: any) => void;
  onApplyAiAssistantSuggestion?: (suggestion: any) => void;
  onRemoveAiAssistantSuggestion?: (suggestion: any) => void;
}

// ─── useDragSort ──────────────────────────────────────────────────────────────
function useDragSort({
  onReorder,
}: {
  onReorder: (fromIndex: number, toIndex: number) => void;
}) {
  const draggingIndex = useRef<number | null>(null);
  const overIndex = useRef<number | null>(null);
  const ghostEl = useRef<HTMLDivElement | null>(null); // DOM node of the rendered ghost div
  const originRect = useRef<DOMRect | null>(null);
  const [dragState, setDragState] = useState<any>(null); // { index, rect, label } | null

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>, index: number, label: string) => {
      e.preventDefault();

      const itemEl = e.currentTarget.closest("[data-drag-item]");
      if (!itemEl) return;
      const rect = itemEl.getBoundingClientRect();
      originRect.current = rect;
      draggingIndex.current = index;
      overIndex.current = index;

      // Render ghost at initial cursor position
      setDragState({
        index,
        label,
        top: e.clientY - rect.height / 2,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });

      const onPointerMove = (e: PointerEvent) => {
        // Move ghost via direct DOM mutation (no re-render needed)
        if (ghostEl.current) {
          ghostEl.current.style.top = `${
            e.clientY - originRect.current.height / 2
          }px`;
          ghostEl.current.style.left = `${
            e.clientX - originRect.current.width / 2
          }px`;
        }

        // Hit-test: find which group item the cursor is over
        if (ghostEl.current) ghostEl.current.style.pointerEvents = "none";
        const target = document.elementFromPoint(e.clientX, e.clientY);
        if (ghostEl.current) ghostEl.current.style.pointerEvents = "";

        if (!target) return;
        const itemEl = target.closest("[data-drag-item]");
        if (!itemEl) return;
        const idx = parseInt(itemEl.dataset.dragItem, 10);
        if (!isNaN(idx) && idx !== overIndex.current) {
          overIndex.current = idx;
          setDragState((prev) => prev && { ...prev, overIndex: idx });
        }
      };

      const onPointerUp = () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        if (
          draggingIndex.current !== null &&
          overIndex.current !== null &&
          draggingIndex.current !== overIndex.current
        ) {
          onReorder(draggingIndex.current, overIndex.current);
        }
        draggingIndex.current = null;
        overIndex.current = null;
        setDragState(null);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [onReorder]
  );

  return { draggingIndex, overIndex, ghostEl, dragState, onPointerDown };
}

const SolutionStep: FC<SolutionStepProps> = ({
  step,
  stepIndex = 0,
  hasPreviousStep = false,
  hasNextStep = false,
  nextStepName = undefined,
  disableInputEditing = false,
  disableInputDragging = false,
  focusedInputNodeKey = null,
  onPreviousStep,
  onNextStep,
  onInputFocus,
  onInputBlur,
  setStep,
  onDelete,
  formData,
  setFormData,
  sidebarDragging = false,
  sidebarOverInput = null,
  importContext = undefined,
  usedInputs = undefined,
  inputUsageByKey = undefined,
  onUsageSelect = undefined,
  getIsVisible = undefined,
  showDefaultValueInputsOnly = false,
  showShortlinksButton = false,
  aiAssistantSuggestions = [],
  isAiAssistantLoading = false,
  onAiAssistantSuggestionClick = undefined,
  onApplyAiAssistantSuggestion = undefined,
  onRemoveAiAssistantSuggestion = undefined,
}) => {
  const resolvedDisableInputEditing =
    disableInputEditing || !!importContext?.disableInputEditing;
  const resolvedDisableInputDragging =
    disableInputDragging || !!importContext?.disableInputDragging;
  const disableStructureEditing = !!importContext?.disableStructureEditing;
  const shouldHideEmptyGroups = disableStructureEditing;
  const hasVisibleInput = useCallback(
    (input: any) => {
      if (!input || input.MergedIntoRef) return false;

      if (
        resolvedDisableInputDragging &&
        getIsVisible &&
        !getIsVisible(input)
      ) {
        return false;
      }

      if (
        showDefaultValueInputsOnly &&
        (input.__kind === "external" || input.__kind === "component-config")
      ) {
        return false;
      }

      return true;
    },
    [getIsVisible, resolvedDisableInputDragging, showDefaultValueInputsOnly]
  );
  const hasMeaningfulInputValue = useCallback((value: any) => {
    const values = Array.isArray(value) ? value : [value];

    return values.some((item) => {
      if (item === null || item === undefined) return false;
      return typeof item === "string" ? item.trim().length > 0 : true;
    });
  }, []);
  const isRequiredInput = useCallback(
    (input: any) =>
      Boolean(
        input?.Required ||
          input?.IsRequired ||
          input?.IsRequiredManual ||
          input?.IsRequiredSMS ||
          input?.IsRequiredEmail ||
          input?.IsRequiredVoice ||
          input?.IsRequiredHTTP
      ),
    []
  );
  const isStepComplete = !disableStructureEditing || (step.inputGroups || [])
    .flatMap((inputGroup: any) => inputGroup.inputs || [])
    .filter((input: any) => hasVisibleInput(input))
    .every((input: any) => {
      if (input.__kind === "external" || !isRequiredInput(input)) return true;
      if (input.__kind === "component-config") {
        return isComponentConfigComplete(input, importContext);
      }

      const values =
        formData?.[input.ComponentUuid]?.[input.Field] ??
        (input.MergedInputRefs || [])
          .map((ref: any) => formData?.[ref.ComponentUuid]?.[ref.Field])
          .find((value: any) => value !== undefined);

      return hasMeaningfulInputValue(values);
    });
  const stepSuggestions = aiAssistantSuggestions.filter(
    (suggestion: any) => {
      const change = suggestion.action?.change;
      return change?.target === "step" && Number(change.stepIndex) === stepIndex;
    }
  );

  const getGroupSuggestions = useCallback(
    (groupId: string) =>
      aiAssistantSuggestions.filter((suggestion: any) => {
        const change = suggestion.action?.change;
        return (
          change?.target === "group" &&
          Number(change.stepIndex) === stepIndex &&
          change.groupId === groupId
        );
      }),
    [aiAssistantSuggestions, stepIndex]
  );

  const getInputSuggestions = useCallback(
    (groupId: string) =>
      aiAssistantSuggestions.filter((suggestion: any) => {
        const change = suggestion.action?.change;
        return (
          change?.target === "input" &&
          Number(change.stepIndex) === stepIndex &&
          change.groupId === groupId
        );
      }),
    [aiAssistantSuggestions, stepIndex]
  );

  // ── group reorder ────────────────────────────────────────────────────────
  const reorderGroups = useCallback(
    (fromIndex: number, toIndex: number) => {
      const reordered = Array.from(step.inputGroups);
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      setStep({ ...step, inputGroups: reordered });
    },
    [step, setStep]
  );

  // ── input reorder / cross-group move ────────────────────────────────────
  const moveInput = useCallback(
    (
      sourceGroupId: string,
      sourceIndex: number,
      destGroupId: string,
      destIndex: number
    ) => {
      const newGroups = step.inputGroups.map((g) => ({
        ...g,
        inputs: [...g.inputs],
      }));
      const srcGroup = newGroups.find((g) => g.id === sourceGroupId);
      const dstGroup = newGroups.find((g) => g.id === destGroupId);
      if (!srcGroup || !dstGroup) return;
      const [moved] = srcGroup.inputs.splice(sourceIndex, 1);
      dstGroup.inputs.splice(destIndex, 0, moved);
      setStep({ ...step, inputGroups: newGroups });
    },
    [step, setStep]
  );

  const getInputMergeRef = useCallback((input: any) => {
    const componentUuid = input?.ComponentUuid;
    const field = input?.Field;
    if (!componentUuid || !field) return null;
    return { ComponentUuid: componentUuid, Field: field };
  }, []);

  const canMergeInputs = useCallback(
    (sourceInput: any, targetInput: any) => {
      if (!sourceInput || !targetInput) return false;
      if (sourceInput?.__kind || targetInput?.__kind) return false;
      if (!sourceInput?.Format || !targetInput?.Format) return false;
      if (sourceInput.Format !== targetInput.Format) return false;
      const sourceRef = getInputMergeRef(sourceInput);
      const targetRef = getInputMergeRef(targetInput);
      if (!sourceRef || !targetRef) return false;
      return !(
        sourceRef.ComponentUuid === targetRef.ComponentUuid &&
        sourceRef.Field === targetRef.Field
      );
    },
    [getInputMergeRef]
  );

  const mergeInputs = useCallback(
    (
      sourceGroupId: string,
      sourceIndex: number,
      targetGroupId: string,
      targetIndex: number
    ) => {
      const newGroups = step.inputGroups.map((g) => ({
        ...g,
        inputs: [...g.inputs],
      }));

      const srcGroup = newGroups.find((g) => g.id === sourceGroupId);
      const dstGroup = newGroups.find((g) => g.id === targetGroupId);
      if (!srcGroup || !dstGroup) return;

      const sourceInput = srcGroup.inputs[sourceIndex];
      const targetInput = dstGroup.inputs[targetIndex];
      if (!canMergeInputs(sourceInput, targetInput)) return;

      const sourceRef = getInputMergeRef(sourceInput);
      const targetRef = getInputMergeRef(targetInput);
      if (!sourceRef || !targetRef) return;

      const mergedRefs = [...(targetInput.MergedInputRefs || [])].filter(
        (ref: any) =>
          !(
            ref?.ComponentUuid === sourceRef.ComponentUuid &&
            ref?.Field === sourceRef.Field
          )
      );
      mergedRefs.push(sourceRef);

      dstGroup.inputs[targetIndex] = {
        ...targetInput,
        MergedInputRefs: mergedRefs,
      };

      srcGroup.inputs[sourceIndex] = {
        ...sourceInput,
        MergedIntoRef: targetRef,
      };

      setStep({ ...step, inputGroups: newGroups });
    },
    [canMergeInputs, getInputMergeRef, setStep, step]
  );

  const groupDrag = useDragSort({ onReorder: reorderGroups });

  // ── input drag state (lifted here so all groups share it) ────────────────
  const draggingInput = useRef<any>(null); // { groupId, index, uuid, label }
  const overInput = useRef<any>(null); // { groupId, index, mode?: "insert" | "merge", targetIndex?: number, mergeCandidateIndex?: number }
  const inputGhostEl = useRef<HTMLDivElement | null>(null);
  const [inputDragState, setInputDragState] = useState<any>(null); // { label, top, left, width, height } | null

  const startInputDrag = useCallback(
    (
      e: React.PointerEvent<HTMLElement>,
      groupId: string,
      inputIndex: number,
      input: any,
      itemRect: DOMRect
    ) => {
      e.preventDefault();
      e.stopPropagation();

      draggingInput.current = {
        groupId,
        index: inputIndex,
        uuid: input.UUID,
        label: input.Label,
      };
      overInput.current = {
        groupId,
        index: inputIndex,
        mode: "insert",
        mergeCandidateIndex: undefined,
      };

      setInputDragState({
        label: input.Label,
        top: e.clientY - itemRect.height / 2,
        left: itemRect.left,
        width: itemRect.width,
        height: itemRect.height,
      });

      const onMove = (e: PointerEvent) => {
        // Move ghost via direct DOM mutation
        if (inputGhostEl.current) {
          inputGhostEl.current.style.top = `${
            e.clientY - itemRect.height / 2
          }px`;
          inputGhostEl.current.style.left = `${
            e.clientX - itemRect.width / 2
          }px`;
        }

        // Hide ghost for hit-test
        if (inputGhostEl.current)
          inputGhostEl.current.style.pointerEvents = "none";
        const target = document.elementFromPoint(e.clientX, e.clientY);
        if (inputGhostEl.current) inputGhostEl.current.style.pointerEvents = "";

        if (!target) return;

        // Over an existing input item?
        const inputEl = target.closest("[data-input-item]");
        if (inputEl) {
          const tGroupId = inputEl.dataset.groupId;
          const tIndex = parseInt(inputEl.dataset.inputItem, 10);
          if (!isNaN(tIndex)) {
            const groups = step.inputGroups || [];
            const srcGroup = groups.find((g: any) => g.id === groupId);
            const dstGroup = groups.find((g: any) => g.id === tGroupId);
            const sourceInput = srcGroup?.inputs?.[inputIndex];
            const targetInput = dstGroup?.inputs?.[tIndex];
            const itemRect = inputEl.getBoundingClientRect();
            const relativeY = e.clientY - itemRect.top;
            const topThirdEnd = itemRect.height * 0.33;
            const bottomThirdStart = itemRect.height * 0.67;
            const mergeMode =
              canMergeInputs(sourceInput, targetInput) &&
              relativeY >= topThirdEnd &&
              relativeY <= bottomThirdStart;
            const insertIdx =
              relativeY < topThirdEnd
                ? tIndex
                : relativeY > bottomThirdStart
                ? tIndex + 1
                : tIndex;
            const nextOver = mergeMode
              ? {
                  groupId: tGroupId,
                  index: insertIdx,
                  mode: "merge",
                  targetIndex: tIndex,
                  mergeCandidateIndex: tIndex,
                }
              : {
                  groupId: tGroupId,
                  index: insertIdx,
                  mode: "insert",
                  targetIndex: undefined,
                  mergeCandidateIndex: undefined,
                };

            if (
              tGroupId !== overInput.current?.groupId ||
              insertIdx !== overInput.current?.index ||
              nextOver.mode !== overInput.current?.mode ||
              nextOver.targetIndex !== overInput.current?.targetIndex ||
              nextOver.mergeCandidateIndex !==
                overInput.current?.mergeCandidateIndex
            ) {
              overInput.current = nextOver;
              setInputDragState((p) => p && { ...p });
            }
          }
          return;
        }

        // Over an empty group's drop zone?
        const zoneEl = target.closest("[data-input-zone]");
        if (zoneEl) {
          const tGroupId = zoneEl.dataset.inputZone;
          const count = parseInt(zoneEl.dataset.inputCount ?? "0", 10);
          if (
            tGroupId !== overInput.current?.groupId ||
            overInput.current?.index !== count
          ) {
            overInput.current = {
              groupId: tGroupId,
              index: count,
              mode: "insert",
              targetIndex: undefined,
              mergeCandidateIndex: undefined,
            };
            setInputDragState((p) => p && { ...p });
          }
        }
      };

      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);

        if (draggingInput.current && overInput.current) {
          const { groupId: srcGroupId, index: srcIndex } =
            draggingInput.current;
          const {
            groupId: dstGroupId,
            index: dstIndex,
            mode,
            targetIndex,
          } = overInput.current;

          if (mode === "merge" && typeof targetIndex === "number") {
            mergeInputs(srcGroupId, srcIndex, dstGroupId, targetIndex);
          } else if (srcGroupId !== dstGroupId || srcIndex !== dstIndex) {
            moveInput(srcGroupId, srcIndex, dstGroupId, dstIndex);
          }
        }

        draggingInput.current = null;
        overInput.current = null;
        setInputDragState(null);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [canMergeInputs, mergeInputs, moveInput, step.inputGroups]
  );

  return (
    <StyledDiv>
      {/* Group drag ghost — portalled to body so it's never clipped */}
      {groupDrag.dragState &&
        ReactDOM.createPortal(
          <div
            ref={(el) => {
              groupDrag.ghostEl.current = el;
            }}
            style={{
              position: "fixed",
              top: groupDrag.dragState.top,
              left: groupDrag.dragState.left,
              width: groupDrag.dragState.width,
              height: groupDrag.dragState.height,
              pointerEvents: "none",
              zIndex: 9999,
              opacity: 0.8,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              paddingLeft: 12,
              fontWeight: 600,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <FontAwesomeIcon
              icon={faGripVertical}
              className="me-2 text-secondary"
            />
            {groupDrag.dragState.label}
          </div>,
          document.body
        )}
      {/* Input drag ghost */}
      {inputDragState &&
        ReactDOM.createPortal(
          <div
            ref={(el) => {
              inputGhostEl.current = el;
            }}
            style={{
              position: "fixed",
              top: inputDragState.top,
              left: inputDragState.left,
              width: inputDragState.width,
              height: inputDragState.height,
              pointerEvents: "none",
              zIndex: 9999,
              opacity: 0.8,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              paddingLeft: 12,
              fontWeight: 600,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <FontAwesomeIcon
              icon={faGripVertical}
              className="me-2 text-secondary"
            />
            {inputDragState.label}
          </div>,
          document.body
        )}
      <div
        className="bg-lillac p-3 rounded-3 position-relative"
        data-step-target={stepIndex}
      >
        <SolutionAiSuggestionMarker
          suggestions={stepSuggestions}
          isLoading={isAiAssistantLoading}
          onSuggestionClick={onAiAssistantSuggestionClick}
          onApplySuggestion={onApplyAiAssistantSuggestion}
          onRemoveSuggestion={onRemoveAiAssistantSuggestion}
        />
        <div className="d-flex align-items-center gap-3 p-3 edit-buttons">
          <div className="border-end me-2"></div>
          <div className="flex-grow-1">
            <div className="text-spaced text-navy text-uppercase fw-bold d-flex align-items-center justify-content-between">
              <div className="flex-grow-1">Step {stepIndex + 1}</div>
            </div>
            <div className="fw-bold fs-1 fw-bold">{step.name}</div>
            <div
              className="text-secondary text-sm"
              style={{ whiteSpace: "pre-line" }}
            >
              {step.description}
            </div>
          </div>
          <div>
            {!disableStructureEditing && (
              <MyModal
                as="offcanvas"
                heading={`Edit Step ${stepIndex + 1}`}
                label={
                  <FontAwesomeIcon
                    className="text-secondary ms-2"
                    icon={faPen}
                  />
                }
                buttonClassName="btn-sm"
              >
                {({ handleClose }) => (
                  <DeferredEditForm
                    value={step}
                    onCommit={setStep}
                    onClose={handleClose}
                  >
                    {(draftStep, setDraftStep) => (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={draftStep.name}
                            onChange={(e) =>
                              setDraftStep({
                                ...draftStep,
                                name: (e.target as HTMLInputElement).value,
                              })
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows={4}
                            value={draftStep.description}
                            onChange={(e) =>
                              setDraftStep({
                                ...draftStep,
                                description: e.target.value,
                              })
                            }
                          />
                        </div>
                        <VisibilityInputs
                          editedVariable={draftStep}
                          setEditedVariable={setDraftStep}
                          inputArguments={usedInputs as any}
                        />
                      </>
                    )}
                  </DeferredEditForm>
                )}
              </MyModal>
            )}
            {!disableStructureEditing && (
              <button
                className="btn btn-link p-0 ms-2 btn-sm"
                onClick={onDelete}
              >
                <FontAwesomeIcon className="text-secondary" icon={faX} />
              </button>
            )}
          </div>
        </div>
        {/* ── Input groups list ── */}
        <div>
          {step.inputGroups.map((inputGroup, index) => {
            if (
              resolvedDisableInputDragging &&
              getIsVisible &&
              !getIsVisible(inputGroup)
            )
              return null;

            if (
              shouldHideEmptyGroups &&
              !(inputGroup.inputs || []).some(hasVisibleInput)
            ) {
              return null;
            }

            const isDragging = groupDrag.draggingIndex.current === index;
            const isOver =
              groupDrag.draggingIndex.current !== null &&
              groupDrag.overIndex.current === index;

            return (
              <div
                key={inputGroup.id}
                data-drag-item={index}
                data-input-group-target={inputGroup.id}
                className="position-relative"
                style={{
                  opacity: isDragging ? 0.4 : 1,
                  outline:
                    isOver && !isDragging ? "2px dashed #6c5ce7" : "none",
                  borderRadius: 8,
                  transition: "outline 0.1s",
                }}
              >
                <SolutionAiSuggestionMarker
                  suggestions={getGroupSuggestions(inputGroup.id)}
                  isLoading={isAiAssistantLoading}
                  onSuggestionClick={onAiAssistantSuggestionClick}
                  onApplySuggestion={onApplyAiAssistantSuggestion}
                  onRemoveSuggestion={onRemoveAiAssistantSuggestion}
                />
                <SolutionStepInputGroup
                  inputGroup={inputGroup}
                  step={step}
                  setStep={setStep}
                  disableInputEditing={resolvedDisableInputEditing}
                  disableInputDragging={resolvedDisableInputDragging}
                  formData={formData}
                  setFormData={setFormData}
                  onGroupDragHandlePointerDown={(e) =>
                    groupDrag.onPointerDown(e, index, inputGroup.name)
                  }
                  onMoveInput={moveInput}
                  draggingInput={draggingInput.current}
                  overInput={overInput.current}
                  onStartInputDrag={startInputDrag}
                  sidebarDragging={sidebarDragging}
                  sidebarOverInput={sidebarOverInput}
                  importContext={importContext}
                  usedInputs={usedInputs}
                  inputUsageByKey={inputUsageByKey}
                  onUsageSelect={onUsageSelect}
                  getIsVisible={getIsVisible}
                  showDefaultValueInputsOnly={showDefaultValueInputsOnly}
                  showShortlinksButton={showShortlinksButton}
                  onInputFocus={onInputFocus}
                  onInputBlur={onInputBlur}
                  focusedInputNodeKey={focusedInputNodeKey}
                  aiAssistantSuggestions={getInputSuggestions(inputGroup.id)}
                  isAiAssistantLoading={isAiAssistantLoading}
                  onAiAssistantSuggestionClick={onAiAssistantSuggestionClick}
                  onApplyAiAssistantSuggestion={onApplyAiAssistantSuggestion}
                  onRemoveAiAssistantSuggestion={onRemoveAiAssistantSuggestion}
                />
              </div>
            );
          })}
        </div>

        {!disableStructureEditing && (
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-colored"
              onClick={() => {
                const newInputGroup = {
                  id: `input-group-${step.inputGroups.length + 1}`,
                  name: `Input Group ${step.inputGroups.length + 1}`,
                  description:
                    "Input group description goes here. Lorem ipsum dolor sit amet consectetur adipisicing elit.",
                  inputs: [],
                };
                setStep({
                  ...step,
                  inputGroups: [...step.inputGroups, newInputGroup],
                });
              }}
            >
              Add input group
            </button>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between mt-4">
          {hasNextStep ? (
            <div className="d-flex align-items-center gap-3">
              <div>
                <AiIcon width={40} className="" />
              </div>
              <div>
                {isStepComplete ? (
                  <>
                    <div className="text-navy fw-bold fs-4">Good job!</div>
                    <div>Everything looks great, continue to the next step.</div>
                    {nextStepName && (
                      <div className="fw-bold">{nextStepName}</div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-navy fw-bold fs-4">
                      Complete this step
                    </div>
                    <div className="text-danger">
                      Complete all required inputs before continuing.
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div />
          )}
          <div className="d-flex gap-3">
            <div>
              {hasPreviousStep && (
                <Button
                  type="button"
                  className="btn bg-black rounded-pill fw-bold px-4 border-0"
                  onClick={onPreviousStep}
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
                  Previous
                </Button>
              )}
            </div>
            <div>
              {hasNextStep ? (
                <Button
                  type="button"
                  className="btn btn-colored-reverse rounded-pill fw-bold px-4 border-0"
                  onClick={onNextStep}
                >
                  Next{" "}
                  <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </StyledDiv>
  );
};

export default SolutionStep;
